import type { DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';

import type { API, StreamingCommand } from '../aiStreamingOrchestratorPluginType';

declare global {
	interface Window {
		__aifcDebuggerEnabled__: boolean | undefined;
		__aifcEditorDebugData__: () => object | undefined;
	}
}

const reset = '\x1b[0m';
const red = '\x1b[31m';
const magenta = '\x1b[35m';
const green = '\x1b[32m';
const blue = '\x1b[34m';

type StreamLogData = {
	actionId?: string;
	adf?: object;
	chunk?: string;
	command?: StreamingCommand;
	early?: boolean;
	finalStream?: boolean;
	message?: string;
	reason?: string;
	sessionId?: string;
	timestamp: Date;
	type: 'Public' | 'Queue' | 'Error';
};

export class DebugLogger {
	private logs: StreamLogData[] = [];
	private MAX_LENGTH = 100;

	constructor(private api: API | undefined) {
		if (fg('platform_editor_editor_debugging_panel')) {
			if (window.top) {
				try {
					window.top.__aifcEditorDebugData__ = () => this.getDebugInformation();
				} catch {
					// Ignore cross-origin errors
				}
			}
		}
	}

	private add(log: StreamLogData) {
		if (fg('platform_editor_editor_debugging_panel')) {
			try {
				if (window.top?.__aifcDebuggerEnabled__) {
					const { type, message } = log;
					// eslint-disable-next-line no-console
					console.log(
						`${magenta}[AIFC]:EditorStreamQueue${reset} ${
							type === 'Public' ? green : type === 'Queue' ? blue : red
						}${type}${reset} ${message}`,
						log,
					);
				}
			} catch {
				// Ignore cross-origin errors
			}
			this.logs.push({
				type: log.type,
				message: log.message,
				timestamp: log.timestamp,
			});
			if (this.logs.length > this.MAX_LENGTH) {
				this.logs.shift(); // Remove the oldest log to maintain a max log
			}
		}
	}

	logPublic(
		message: string,
		data?: Pick<StreamLogData, 'command' | 'actionId' | 'chunk' | 'sessionId'>,
	): void {
		this.add({ type: 'Public', message, ...data, timestamp: new Date() });
	}

	logError(message: string, data?: Pick<StreamLogData, 'actionId' | 'chunk'>): void {
		this.add({ type: 'Error', message, ...data, timestamp: new Date() });
	}

	logQueueState(
		message: string,
		data?: Pick<
			StreamLogData,
			'command' | 'actionId' | 'chunk' | 'finalStream' | 'adf' | 'reason' | 'early' | 'sessionId'
		>,
	): void {
		this.add({ type: 'Queue', message, ...data, timestamp: new Date() });
	}

	// Current ADF at the time of logging so we can recreate the scenario
	private getAdf() {
		if (!this.api) {
			return undefined;
		}
		let doc: DocNode | undefined;
		this.api.core.actions.execute(({ tr }) => {
			doc = tr.doc.toJSON();
			return null;
		});
		return doc;
	}

	getDebugInformation() {
		return {
			logs: this.logs,
			adf: this.getAdf(),
			selection: this.api?.selection?.actions.getSelectionFragment() ?? undefined,
			localIds: this.api?.selection?.actions.getSelectionLocalIds() ?? undefined,
		};
	}
}
